import React, { useEffect } from 'react'
import { isEqual } from 'lodash'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowLeft, faUser } from '@fortawesome/free-solid-svg-icons'
import Button from 'react-bootstrap/Button'
import { useAppDispatch, useAppSelector } from '../hooks'
import {
  fetchPersonas,
  selectPersona,
  resetToDefault,
  loadPersistedPersona,
  createNewThread,
} from '../features/completions/slice'
import {
  getPersonas,
  getPersonasLoading,
  getPersonasError,
  getSelectedPersona,
} from '../features/completions/selectors'
import { Persona } from '../types'
import Loading from './Loading'
import { removeAllFiles } from '../features/files/slice'
import Sparkles from '../assets/Sparkles'

interface Props {
  onBack: () => void
}

function PersonaSelection({ onBack }: Props) {
  const dispatch = useAppDispatch()
  const personas = useAppSelector(getPersonas, isEqual)
  const loading = useAppSelector(getPersonasLoading, isEqual)
  const error = useAppSelector(getPersonasError, isEqual)
  const selectedPersona = useAppSelector(getSelectedPersona, isEqual)

  useEffect(() => {
    dispatch(fetchPersonas())
    dispatch(loadPersistedPersona())
  }, [dispatch])

  const handlePersonaSelect = async (persona: Persona) => {
    try {
      await dispatch(createNewThread()).unwrap()
      dispatch(selectPersona(persona))
      dispatch(removeAllFiles())
      onBack()
    } catch {
      // The actions already show a toast error
    }
  }

  const handleResetToDefault = async () => {
    try {
      await dispatch(createNewThread()).unwrap()

      dispatch(resetToDefault())
      dispatch(removeAllFiles())
      onBack()
    } catch {
      // The actions already show a toast error
    }
  }

  if (loading) {
    return (
      <div className='pilot-persona-selection'>
        <div className='persona-header'>
          <Button variant='outline-primary' onClick={onBack} className='back-button'>
            <FontAwesomeIcon icon={faArrowLeft} />
            <span>Back to Chat</span>
          </Button>
          <h3>Select a Persona</h3>
        </div>
        <div className='persona-loading'>
          <Loading />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className='pilot-persona-selection'>
        <div className='persona-header'>
          <Button variant='outline-primary' onClick={onBack} className='back-button'>
            <FontAwesomeIcon icon={faArrowLeft} />
            <span>Back to Chat</span>
          </Button>
          <h3>Select a Persona</h3>
        </div>
        <div className='persona-error'>
          <p>Error loading personas: {error}</p>
          <Button variant='primary' onClick={() => dispatch(fetchPersonas())}>
            Retry
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className='pilot-persona-selection'>
      <div className='persona-header'>
        <Button variant='outline-primary' onClick={onBack} className='back-button'>
          <FontAwesomeIcon icon={faArrowLeft} />
          <span>Back to Chat</span>
        </Button>
        <h3>Select a Persona</h3>
      </div>

      <div className='persona-grid'>
        <div
          className={`persona-card ${!selectedPersona ? 'selected' : ''}`}
          onClick={handleResetToDefault}
        >
          <div className='persona-avatar'>
            <div className='default-avatar'>
              <Sparkles />
            </div>
          </div>
          <div className='persona-name'>Solo</div>
          <div className='persona-description'>Default AI assistant</div>
        </div>

        {personas.map((persona: Persona) => (
          <div
            key={persona.id}
            className={`persona-card ${selectedPersona?.id === persona.id ? 'selected' : ''}`}
            onClick={() => handlePersonaSelect(persona)}
          >
            <div className='persona-avatar'>
              {persona.avatarUrl ? (
                <img src={persona.avatarUrl} alt={persona.name} className='persona-image' />
              ) : (
                <div className='default-avatar'>
                  <FontAwesomeIcon icon={faUser} />
                </div>
              )}
            </div>
            <div className='persona-name'>{persona.name}</div>
            <div className='persona-description'>{persona.description || 'AI assistant'}</div>
          </div>
        ))}
      </div>

      {personas.length === 0 && (
        <div className='no-personas'>
          <p>No personas available at the moment.</p>
          <Button variant='outline-primary' onClick={() => dispatch(fetchPersonas())}>
            Refresh
          </Button>
        </div>
      )}
    </div>
  )
}

export default PersonaSelection
